import { useState, useEffect } from "react";
import { useAuth } from "@/App";
import { toast } from "sonner";
import { Loader2, Gift, Users, DollarSign, TrendingUp } from "lucide-react";

const ReferralAnalytics = () => {
  const { authAxios } = useAuth();
  const [referrals, setReferrals] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    completed: 0,
    totalRevenue: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReferralData();
  }, []);

  const fetchReferralData = async () => {
    try {
      const response = await authAxios.get('/admin/referrals');
      setReferrals(response.data.referrals || []);
      setStats(response.data.stats || stats);
    } catch (e) {
      // If endpoint doesn't exist, show empty
      console.log("Admin referrals endpoint may not exist yet");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 text-emerald-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="font-orbitron text-xl text-white flex items-center gap-2">
          <Gift className="w-5 h-5 text-emerald-400" />
          Referral Analytics
        </h2>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="cyber-card p-4 border-emerald-400/30">
          <Users className="w-5 h-5 text-emerald-400 mb-2" />
          <div className="font-orbitron text-2xl text-white">{stats.total}</div>
          <div className="font-rajdhani text-xs text-gray-500">Total Referrals</div>
        </div>
        <div className="cyber-card p-4 border-yellow-400/30">
          <Gift className="w-5 h-5 text-yellow-400 mb-2" />
          <div className="font-orbitron text-2xl text-white">{stats.pending}</div>
          <div className="font-rajdhani text-xs text-gray-500">Pending</div>
        </div>
        <div className="cyber-card p-4 border-neon-green/30">
          <TrendingUp className="w-5 h-5 text-neon-green mb-2" />
          <div className="font-orbitron text-2xl text-white">{stats.completed}</div>
          <div className="font-rajdhani text-xs text-gray-500">Converted</div>
        </div>
        <div className="cyber-card p-4 border-neon-pink/30">
          <DollarSign className="w-5 h-5 text-neon-pink mb-2" />
          <div className="font-orbitron text-2xl text-white">${stats.totalRevenue?.toFixed(2) || '0.00'}</div>
          <div className="font-rajdhani text-xs text-gray-500">Rewards Given</div>
        </div>
      </div>

      {/* Info Card */}
      <div className="cyber-card p-4 border-emerald-400/30">
        <h3 className="font-orbitron text-sm text-emerald-400 mb-2">How Referrals Work</h3>
        <ul className="space-y-2 font-rajdhani text-sm text-gray-400">
          <li>• Each user gets a unique referral code upon registration</li>
          <li>• New users who sign up with a code get <span className="text-neon-pink">15% off</span> their first purchase</li>
          <li>• Referrers earn <span className="text-neon-green">10% credit</span> when their referral makes a purchase</li>
          <li>• Credits can be applied to future purchases</li>
        </ul>
      </div>

      {/* Referrals List */}
      {referrals.length > 0 ? (
        <div className="cyber-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left p-4 font-orbitron text-xs text-gray-400">Date</th>
                  <th className="text-left p-4 font-orbitron text-xs text-gray-400">Referrer Code</th>
                  <th className="text-left p-4 font-orbitron text-xs text-gray-400">Referred User</th>
                  <th className="text-left p-4 font-orbitron text-xs text-gray-400">Status</th>
                  <th className="text-left p-4 font-orbitron text-xs text-gray-400">Reward</th>
                </tr>
              </thead>
              <tbody>
                {referrals.map(ref => (
                  <tr key={ref.id} className="border-b border-white/5 hover:bg-white/5">
                    <td className="p-4 font-rajdhani text-sm text-gray-300">
                      {formatDate(ref.created_at)}
                    </td>
                    <td className="p-4 font-orbitron text-xs text-neon-blue">
                      {ref.referral_code}
                    </td>
                    <td className="p-4 font-rajdhani text-sm text-gray-300">
                      {ref.referred_name || ref.referred_user_id?.slice(0, 8)}
                    </td>
                    <td className="p-4">
                      <span className={`px-2 py-1 text-xs font-orbitron uppercase ${
                        ref.status === 'completed' 
                          ? 'bg-neon-green/20 text-neon-green' 
                          : 'bg-yellow-400/20 text-yellow-400'
                      }`}>
                        {ref.status}
                      </span>
                    </td>
                    <td className="p-4 font-orbitron text-sm text-neon-pink">
                      ${ref.reward_amount?.toFixed(2) || '0.00'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="text-center py-12 cyber-card">
          <Gift className="w-12 h-12 text-gray-600 mx-auto mb-4" />
          <p className="font-rajdhani text-gray-500">No referrals yet. Share the program to get started!</p>
        </div>
      )}
    </div>
  );
};

export default ReferralAnalytics;
